import { call, put, select } from 'redux-saga/effects';
import * as actions from './actions';
import axios from 'axios';
import {
  flattenExpressionForRequest,
  mapExpressionFromResposne
} from 'store/helpers';
import produce from 'immer';
import {
  updatePageNo,
  updateTotalCount
} from 'store/helpers/tableAndToolbar/actions';
import { getEnvVar } from 'helpers';
import { waitForGetEventMetaData } from 'store/event/sagas';
import { ProductType } from 'constants.js';
import { updateListFilters } from './actions';

const apiRoot = `${window._env_?.REACT_APP_LIST_API_PATH}`;

const getListApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}list/tenants/${tenantId}/context/${businessContextId}`;

const getProfileApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}profile/tenants/${tenantId}/context/${businessContextId}`;

const getSubscriberApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}profile/tenants/${tenantId}/context/${businessContextId}`;

export const getAllListsAsync = (tenantId, businessContextId) =>
  axios
    .get(`${getListApiRoot(tenantId, businessContextId)}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getAllSegmentedListsAsync = (tenantId, businessContextId) =>
  axios
    .get(`${getListApiRoot(tenantId, businessContextId)}/segment`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getPaginatedListsAsync = (
  tenantId,
  businessContextId,
  queryParams,
  requestBody
) =>
  axios
    .post(
      `${getListApiRoot(tenantId, businessContextId)}/search?pageNumber=${
        queryParams.pageNumber
      }&pageSize=${queryParams.pageSize}`,
      requestBody
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });

export const getCurrentListAsync = (tenantId, businessContextId, listId) =>
  axios
    .get(`${getListApiRoot(tenantId, businessContextId)}/listId/${listId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const createListAsync = (tenantId, businessContextId, requestBody) =>
  axios
    .post(
      `${getListApiRoot(tenantId, businessContextId)}`,
      requestBody
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateListNameAsync = (tenantId, businessContextId, data) =>
  axios
    .put(
      `${getListApiRoot(tenantId, businessContextId)}/listId/${data.listId}`,
      { name: data.listName }
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteListAsync = (tenantId, businessContextId, listId) =>
  axios
    .delete(`${getListApiRoot(tenantId, businessContextId)}/listId/${listId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteAllSubscribersAsync = (tenantId, businessContextId, listId) =>
  axios
    .put(`${getListApiRoot(tenantId, businessContextId)}/listId/${listId}/purge`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getActiveSubscribersAsync = (
  tenantId,
  businessContextId,
  listId,
  queryParams,
  requestBody
) =>
  axios
    .post(
      `${getProfileApiRoot(
        tenantId,
        businessContextId
      )}/listId/${listId}/search?pageNumber=${
        queryParams.pageNumber
      }&pageSize=${queryParams.pageSize}`,
      requestBody
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });

export const getSuppressedSubscribersAsync = (
  tenantId,
  businessContextId,
  listId,
  queryParams,
  requestBody
) =>
  axios
    .post(
      `${getProfileApiRoot(
        tenantId,
        businessContextId
      )}/listId/${listId}/suppress/search?pageNumber=${
        queryParams.pageNumber
      }&pageSize=${queryParams.pageSize}`,
      requestBody
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });

export const removeSubscriberAsync = (tenantId, businessContextId, data) =>
  axios
    .delete(
      `${getProfileApiRoot(tenantId, businessContextId)}/profileId/${
        data.profileId
      }/listId/${data.listId}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const uploadSubscribersFileAsync = async (
  tenantId,
  businessContextId,
  data
) => {
  let formData = new FormData();
  formData.append('file', data.file);
  await axios
    .post(
      `${getListApiRoot(tenantId, businessContextId)}/listId/${data.listId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export const getPaginatedSegmentedListsAsync = (
  tenantId,
  businessContextId,
  listId,
  queryParams,
  requestBody
) =>
  axios
    .post(
      `${getListApiRoot(
        tenantId,
        businessContextId
      )}/listId/${listId}/segment/search?pageNumber=${
        queryParams.pageNumber
      }&pageSize=${queryParams.pageSize}`,
      requestBody
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });

export const getSegmentedListAsync = (
  tenantId,
  businessContextId,
  segmentedListId
) =>
  axios
    .get(
      `${getListApiRoot(
        tenantId,
        businessContextId
      )}/segmentListId/${segmentedListId}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const createSegmentedListAsync = (
  tenantId,
  businessContextId,
  listId,
  segmentedList
) =>
  axios
    .post(
      `${getListApiRoot(tenantId, businessContextId)}/listId/${listId}/segment`,
      segmentedList
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateSegmentedListAsync = (
  tenantId,
  businessContextId,
  segmentListId,
  segmentedList
) =>
  axios
    .put(
      `${getListApiRoot(
        tenantId,
        businessContextId
      )}/segmentListId/${segmentListId}`,
      segmentedList
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteSegmentedListAsync = (
  tenantId,
  businessContextId,
  segmentListId
) =>
  axios
    .delete(
      `${getListApiRoot(
        tenantId,
        businessContextId
      )}/segmentListId/${segmentListId}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getSampleFileAsync = (tenantId, businessContextId) =>
  axios({
    url: `${getListApiRoot(tenantId, businessContextId)}/sampleModelFile`,
    method: 'GET',
    responseType: 'blob'
  })
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getImportStatusListAsync = (
  tenantId,
  businessContextId,
  listId,
  queryParams,
  requestBody
) =>
  axios
    .post(
      `${getListApiRoot(
        tenantId,
        businessContextId
      )}/listId/${listId}/state/search?pageNumber=${
        queryParams.pageNumber
      }&pageSize=${queryParams.pageSize}`,
      requestBody
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });

export const getFileNameStatusListAsync = (
  tenantId,
  businessContextId,
  queryParams
) => {
  let stateValues = queryParams.states
    .map(state => `&state=${state}`)
    .reduce((prev, current) => prev + current);
  return axios
    .get(
      `${getListApiRoot(tenantId, businessContextId)}/listId/${
        queryParams.listId
      }/state?fileName=${queryParams.fileName}${stateValues}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export const suppressProfileAsync = (tenantId, businessContextId, data) =>
  axios
    .post(
      `${getProfileApiRoot(tenantId, businessContextId)}/profileId/${
        data.profileId
      }/listId/${data.listId}/suppress`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const activateProfileAsync = (tenantId, businessContextId, data) =>
  axios
    .post(
      `${getProfileApiRoot(tenantId, businessContextId)}/profileId/${
        data.profileId
      }/listId/${data.listId}/activate`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getErrorFileAsync = (tenantId, businessContextId, data) =>
  axios
    .get(
      `${getListApiRoot(tenantId, businessContextId)}/errorFile/${
        data.errorFileId
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const createSubscriberListAsync = (tenantId, businessContextId, listId, requestBody) => 
  axios.post(
    `${getSubscriberApiRoot(tenantId, businessContextId)}/listId/${listId}/profile/create`,
    requestBody
  )
  .then(response => response.data)
  .catch(err => { throw err; });

export const updateSubscriberListAsync = (tenantId, businessContextId, listId, profileId, requestBody) => 
  axios.put(
    `${getSubscriberApiRoot(tenantId, businessContextId)}/listId/${listId}/profileId/${profileId}/update`,
    requestBody
  )
  .then(response => response.data)
  .catch(err => { throw err; });

export const getAdditionalProfileListAsync = (tenantId, businessContextId, listId, profileId) =>
  axios
    .get(
      `${getProfileApiRoot(tenantId, businessContextId)}/profileId/${profileId}?listId=${listId}`
    )
    .then(response => response.data)
    .catch(err => { throw err; });

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const currentList = store =>
  store.lists ? store.lists.subscribersList.listsData : [];
const segmentedLists = store =>
  store.lists ? store.lists.segmentedList.listsData : [];
const eventMetaData = store =>
  store.lists ? store.events.eventMetaData.data : {};
const currentSegmentedList = store =>
  store.lists ? store.lists.segmentedList.currentList : {};
const allLists = store => (store.lists ? store.lists.allLists.current : []);
const allSegmentedLists = store =>
  store.lists ? store.lists.allSegmentedLists.current : [];
const toolbarPageNo = store =>
  store.tableAndToolbar ? store.tableAndToolbar.pageNo : 0;
const toolbarPageSize = store => (store.user ? store.tableAndToolbar.rowsPerPage : 10);
const toolbarSearchInput = store =>
  store.tableAndToolbar ? store.tableAndToolbar.searchInput : '';
const toolbarSortBy = store =>
  store.tableAndToolbar ? store.tableAndToolbar.sortBy : '';
const toolbarSortOrder = store =>
  store.tableAndToolbar ? store.tableAndToolbar.sortOrder : 'ASC';
const toolbarTotalCount = store =>
  store.tableAndToolbar ? store.tableAndToolbar.totalCount : 0;

const searchInput = store =>
  store.lists.filters ? store.lists.filters?.searchInput : '';
const sortBy = store =>
  store.lists.filters ? store.lists.filters?.sortBy : 'name';
const sortOrder = store =>
  store.lists.filters ? store.lists.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.lists.filters ? store.lists.filters?.rowsPerPage : 10;
const pageNo = store =>
  store.lists.filters ? store.lists.filters?.pageNo : 0;
const totalCount = store =>
  store.lists.filters ? store.lists.filters?.totalCount : 0;
const filterTemplates = store =>
  store.lists.filters ? store.lists.filters?.filterTemplates : [];

function* updateToolbarPageNumber() {
  let count = (yield select(toolbarTotalCount)) - 1;
  let pageNumber = yield select(toolbarPageNo);
  let pageCount = yield select(toolbarPageSize);
  if (count && pageNumber >= count / pageCount) {
    pageNumber = pageNumber - 1;
    yield put(updatePageNo(pageNumber));
  }
}

function* updatePageNumber() {
  let count = (yield select(totalCount)) - 1;
  let pageNumber = yield select(pageNo);
  let pageCount = yield select(rowsPerPage);
  if (count && pageNumber >= count / pageCount) {
    pageNumber = pageNumber - 1;
    yield put(updateListFilters({ pageNo: pageNumber }));
  }
}

export function* getCurrentListIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let list = yield call(
      getCurrentListAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield put(actions.gettingListSucceeded(list));
  } catch (ex) {
    yield put(actions.gettingListFailed(ex));
  }
}

export function* getPaginatedListsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let list = yield select(currentList);
    let pageNumber = yield select(pageNo);
    let pageCount = yield select(rowsPerPage);
    let searchText = yield select(searchInput);
    let sortByValue = yield select(sortBy);
    let order = yield select(sortOrder);
    let filterHierarchy = yield select(filterTemplates);
    let queryParams = {
      pageNumber: pageNumber,
      pageSize: pageCount
    };
    let requestBody = {
      searchText: searchText,
      sortBy: sortByValue,
      sortOrder: order,
      ...(filterHierarchy?.length > 0 && { searchScopes: filterHierarchy })
    };
    if (list.length === 0 || payload.force === true) {
      let listsData = yield call(
        getPaginatedListsAsync,
        tenantId,
        businessContextId,
        queryParams,
        requestBody
      );
      yield put(updateListFilters({ totalCount: listsData.totalRecords }));
      list = listsData.data;
    }
    yield put(actions.gettingPaginatedListsSucceeded(list));
  } catch (ex) {
    yield put(actions.gettingPaginatedListsFailed(ex));
  }
}

export function* createList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let requestBody = { name: payload };
    let list = yield call(
      createListAsync,
      tenantId,
      businessContextId,
      requestBody
    );
    yield put(actions.creatingListSucceeded(list));
  } catch (ex) {
    yield put(actions.creatingListFailed(ex));
  }
}

export function* updateCurrentListName(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let list = yield call(
      updateListNameAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield put(actions.updatingListNameSucceeded(list));
  } catch (ex) {
    yield put(actions.updatingListNameFailed(ex));
  }
}

export function* deleteCurrentList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let list = yield call(
      deleteListAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield call(updatePageNumber);
    yield put(actions.deletingListSucceeded());
  } catch (ex) {
    yield put(actions.deletingListFailed(ex));
  }
}

export function* deleteAllSubscribers(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let subscribers = yield call(
      deleteAllSubscribersAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield call(updateToolbarPageNumber);
    yield put(actions.deletingAllSubscribersSucceeded());
  } catch (ex) {
    yield put(actions.deletingAllSubscribersFailed(ex));
  }
}

export function* getActiveSubscribersForList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let pageNumber = yield select(toolbarPageNo);
    let pageCount = yield select(toolbarPageSize);
    let searchText = yield select(toolbarSearchInput);
    let sortByValue = yield select(toolbarSortBy);
    let order = yield select(toolbarSortOrder);
    let queryParams = {
      pageNumber: pageNumber,
      pageSize: pageCount
    };
    let requestBody = {
      searchText: searchText,
      sortBy: sortByValue,
      sortOrder: order
    };
    let subscriberList = yield call(
      getActiveSubscribersAsync,
      tenantId,
      businessContextId,
      payload,
      queryParams,
      requestBody
    );
    yield put(updateTotalCount(subscriberList.totalRecords));
    yield put(actions.gettingActiveSubscribersSucceeded(subscriberList.data));
  } catch (ex) {
    yield put(actions.gettingActiveSubscribersFailed(ex));
  }
}

export function* createSubscriberList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let listId = payload.listId;
    let requestBody = payload.subscriberList;
    let subscriberList = yield call(
      createSubscriberListAsync,
      tenantId,
      businessContextId,
      listId,
      requestBody
    );
    yield put(actions.creatingSubscriberListSucceeded(subscriberList));
  } catch (ex) {
    yield put(actions.creatingSubscriberListFailed(ex));
  }
}

export function* updateSubscriberList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let listId = payload.listId;
    let profileId = payload.profileId;
    let requestBody = payload.subscriberList;
    let subscriberList = yield call(
      updateSubscriberListAsync,
      tenantId,
      businessContextId,
      listId,
      profileId,
      requestBody
    );
    yield put(actions.updatingSubscriberListSucceeded(subscriberList));
  } catch (ex) {
    yield put(actions.updatingSubscriberListFailed(ex));
  }
}

export function* getAdditionalProfileList(action) {
    const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let listId = payload.listId;
    let profileId = payload.profileId;
    let profileList = yield call(
      getAdditionalProfileListAsync,
      tenantId,
      businessContextId,
      listId,
      profileId
    );
    yield put(actions.gettingAdditionalProfileListSucceeded(profileList));
  } catch (ex) {
    yield put(actions.gettingAdditionalProfileListFailed(ex));
  }
}

export function* getSuppressedSubscribersForList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let pageNumber = yield select(toolbarPageNo);
    let pageCount = yield select(toolbarPageSize);
    let searchText = yield select(toolbarSearchInput);
    let sortByValue = yield select(toolbarSortBy);
    let order = yield select(toolbarSortOrder);
    let queryParams = {
      pageNumber: pageNumber,
      pageSize: pageCount
    };
    let requestBody = {
      searchText: searchText,
      sortBy: sortByValue,
      sortOrder: order
    };
    let subscriberList = yield call(
      getSuppressedSubscribersAsync,
      tenantId,
      businessContextId,
      payload,
      queryParams,
      requestBody
    );
    yield put(updateTotalCount(subscriberList.totalRecords));
    yield put(
      actions.gettingSuppressedSubscribersSucceeded(subscriberList.data)
    );
  } catch (ex) {
    yield put(actions.gettingSuppressedSubscribersFailed(ex));
  }
}

export function* removeSubscriberFromList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let subscriber = yield call(
      removeSubscriberAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield call(updateToolbarPageNumber);
    yield put(actions.removeSubscriberSucceeded());
  } catch (ex) {
    yield put(actions.removeSubscriberFailed(ex));
  }
}

export function* uploadSubscribersFile(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let subscriber = yield call(
      uploadSubscribersFileAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield put(actions.importFileSucceeded(subscriber));
  } catch (ex) {
    yield put(actions.importFileFailed(ex));
  }
}

export function* getPaginatedSegmentedListsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentSegmentedLists = yield select(segmentedLists);
    let pageNumber = yield select(toolbarPageNo);
    let pageCount = yield select(toolbarPageSize);
    let searchText = yield select(toolbarSearchInput);
    let sortByValue = yield select(toolbarSortBy);
    let order = yield select(toolbarSortOrder);
    let queryParams = {
      pageNumber: pageNumber,
      pageSize: pageCount
    };
    let requestBody = {
      searchText: searchText,
      sortBy: sortByValue,
      sortOrder: order
    };
    if (getEnvVar(
      'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
        'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) {
      currentSegmentedLists = yield call(
        getPaginatedSegmentedListsAsync,
        tenantId,
        businessContextId,
        payload,
        queryParams,
        requestBody
      );
    }
    yield put(updateTotalCount(currentSegmentedLists.totalRecords));
    yield put(
      actions.gettingPaginatedSegmentedListsSucceeded(
        currentSegmentedLists.data
      )
    );
  } catch (ex) {
    yield put(actions.gettingPaginatedSegmentedListsFailed(ex));
  }
}

export function* deleteCurrentSegmentedList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    if (getEnvVar(
      'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
        'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) {
      yield call(
        deleteSegmentedListAsync,
        tenantId,
        businessContextId,
        payload
      );
    }
    yield call(updateToolbarPageNumber);
    yield put(actions.deletingSegmentedListSucceeded());
  } catch (ex) {
    yield put(actions.deletingSegmentedListFailed(ex));
  }
}

export function* createSegmentedListForList(action) {
  const { payload = {} } = action;
  let headers = yield select(eventMetaData);
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentSegmentedList = flattenExpressionForRequest(
      payload.segmentedList.segmentDefinition,
      headers
    );
    let segmentedList = {};
    if (getEnvVar(
      'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
        'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) {
      segmentedList = yield call(
        createSegmentedListAsync,
        tenantId,
        businessContextId,
        payload.listId,
        {
          name: payload.segmentedList.name,
          segmentDefinition: currentSegmentedList
        }
      );
    }
    segmentedList = produce(segmentedList, draftList => {
      draftList.segmentDefinition = mapExpressionFromResposne(
        draftList?.segmentDefinition || {},
        headers
      );
    });
    yield put(actions.creatingSegmentedListSucceeded(segmentedList));
  } catch (ex) {
    yield put(actions.creatingSegmentedListFailed(ex));
  }
}

export function* getSegmentedList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let segmentedList = yield select(currentSegmentedList);
    yield call(waitForGetEventMetaData, {
      schemaIdentifier: segmentedList.schemaIdentifier,
      productName: segmentedList.productName,
      workflow: 'template',
      productType: ProductType.List
    });
    let headers = yield select(eventMetaData);
    if (getEnvVar(
      'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
        'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) {
      segmentedList = yield call(
        getSegmentedListAsync,
        tenantId,
        businessContextId,
        payload
      );
    }
    segmentedList = produce(segmentedList, draftList => {
      draftList.segmentDefinition = mapExpressionFromResposne(
        draftList?.segmentDefinition || {},
        headers
      );
    });
    yield put(actions.gettingSegmentedListSucceeded(segmentedList));
  } catch (ex) {
    yield put(actions.gettingSegmentedListFailed(ex));
  }
}

export function* updateSegmentedListForList(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let headers = yield select(eventMetaData);
    let currentSegmentedList = flattenExpressionForRequest(
      payload.segmentedList.segmentDefinition,
      headers
    );
    let segmentedList = {};
    if (getEnvVar(
      'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
        'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) {
      segmentedList = yield call(
        updateSegmentedListAsync,
        tenantId,
        businessContextId,
        payload.segmentedListId,
        {
          name: payload.segmentedList.name,
          segmentDefinition: currentSegmentedList
        }
      );
    }
    segmentedList = produce(segmentedList, draftList => {
      draftList.segmentDefinition = mapExpressionFromResposne(
        draftList?.segmentDefinition || {},
        headers
      );
    });
    yield put(actions.updatingSegmentedListSucceeded(segmentedList));
  } catch (ex) {
    yield put(actions.updatingSegmentedListFailed(ex));
  }
}

export function* suppressCurrentSubscriber(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    yield call(suppressProfileAsync, tenantId, businessContextId, payload);
    yield call(updateToolbarPageNumber);
    yield put(actions.suppressSubscriberSucceeded());
  } catch (ex) {
    yield put(actions.suppressSubscriberFailed(ex));
  }
}

export function* activateCurrentSubscriber(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    yield call(activateProfileAsync, tenantId, businessContextId, payload);
    yield call(updateToolbarPageNumber);
    yield put(actions.activateSubscriberSucceeded());
  } catch (ex) {
    yield put(actions.activateSubscriberFailed(ex));
  }
}

export function* getSampleCsvFile(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let response = yield call(getSampleFileAsync, tenantId, businessContextId);
    yield put(actions.gettingSampleFileSucceeded(response));
  } catch (ex) {
    yield put(actions.gettingSampleFileFailed(ex));
  }
}

export function* getImportStatusListForListId(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let pageNumber = yield select(toolbarPageNo);
    let pageCount = yield select(toolbarPageSize);
    let searchText = yield select(toolbarSearchInput);
    let sortByValue = yield select(toolbarSortBy);
    let order = yield select(toolbarSortOrder);
    let queryParams = {
      pageNumber: pageNumber,
      pageSize: pageCount
    };
    let requestBody = {
      searchText: searchText,
      sortBy: sortByValue,
      sortOrder: order
    };
    let currentList = yield call(
      getImportStatusListAsync,
      tenantId,
      businessContextId,
      payload,
      queryParams,
      requestBody
    );
    yield put(updateTotalCount(currentList.totalRecords));
    yield put(actions.gettingImportStatusListSucceeded(currentList.data));
  } catch (ex) {
    yield put(actions.gettingImportStatusListFailed(ex));
  }
}

export function* getErrorCsvFile(action) {
  let { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let file = yield call(
      getErrorFileAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield put(actions.gettingErrorFileSucceeded(file));
  } catch (ex) {
    yield put(actions.gettingErrorFileFailed(ex));
  }
}

export function* getCurrentFileNameStatus(action) {
  let { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield call(
      getFileNameStatusListAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield put(actions.gettingFileNameImportStatusSucceeded(currentList));
  } catch (ex) {
    yield put(actions.gettingFileNameImportStatusFailed(ex));
  }
}

export function* getAllLists(action) {
  let { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(allLists);
    if (payload.force || currentList.length === 0) {
      currentList = yield call(getAllListsAsync, tenantId, businessContextId);
    }
    yield put(actions.gettingAllListsSucceeded(currentList));
  } catch (ex) {
    yield put(actions.gettingAllListsFailed(ex));
  }
}

export function* getAllSegmentedLists(action) {
  let { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(allSegmentedLists);
    if (
      (getEnvVar(
        'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
          'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) &&
      (payload.force || currentList.length === 0)
    ) {
      currentList = yield call(
        getAllSegmentedListsAsync,
        tenantId,
        businessContextId
      );
    }
    yield put(actions.gettingAllSegmentedListsSucceeded(currentList));
  } catch (ex) {
    yield put(actions.gettingAllSegmentedListsFailed(ex));
  }
}
